<template>
  <b-modal
    id="user-receivable-info"
    :modal-class="modalClasses"
    :hide-footer="true"
    ref="userReceivableInfo"
    @shown="shown"
    @hidden="hidden"
  >
    <template v-slot:modal-title>
      {{ stepTitle }}
    </template>

    <template v-slot:default>
      <slot name="description"></slot>
      <AlertWraper
        :content="errors"
        type="error"
      />
      <!-- INIT STEP -->
      <div
        class="font-size-small text-center"
        v-if="step === 'init'"
      >
        <p>
          Para que você possa vender produtos e receber através da Scriv, precisamos de algumas informações suas.
        </p>
        <p>
          Preencha o formulário a seguir para completar seu cadastro de recebedor.
        </p>
        <p>
          Devido à algumas regras do Banco Central tentando coibir fraudes online, nosso parceiro de processamento de pagamentos fará o credenciamento das informações e, assim que o cadastro for aprovado, você já poderá fazer suas vendas.
        </p>
        <p>
          Essa funcionalidade ainda está em fase beta de funcionamento. Em caso de problemas, entre em contato conosco nas nossas redes sociais ou em time@scriv.com.br.
        </p>
        <div>
          <Button
            class="btn-primary btn-sm"
            @click="changeStep('user-info')"
          >
            Iniciar
          </Button>
        </div>
      </div> <!-- END INIT STEP -->
      <!-- USER INFO STEP -->
      <div
        class="text-center font-size-small"
        v-if="step === 'user-info'"
      >
        <p>
          Por favor, nos informe alguns dados pessoais.
        </p>
        <div class="default-form">
            <div class="form-group default-input">
              <TheMask
                :mask="'###.###.###-##'"
                v-model="$v.recipient.document.$model"
                placeholder=""
                class="form-control with-label"
              />
              <label>CPF</label>
            </div>
            <div class="form-group default-input">
                <TheMask
                  :mask="'(##) #####-####'"
                  v-model="$v.recipient.phone.$model"
                  placeholder=""
                  class="form-control with-label"
                />
                <label>Celular</label>
              </div>
          </div>
          <div class="mt-3">
            <Button
              class="btn-primary btn-sm"
              @click="changeStep('bank-accounts')"
              :disabled="$v.$invalid"
            >
              Avançar
            </Button>
          </div>
      </div> <!-- END USER INFO STEP -->
      <!-- BANK ACCOUNT STEP -->
      <div
        class="font-size-small text-center"
        v-if="step === 'bank-accounts'"
      >
        <!-- STEP LOADER -->
        <div
          class="text-center"
          v-if="loader"
        >
          <Loader
            color="color2"
            size="big"
          />
        </div> <!-- END STEP LOADER -->
        <template v-else>
          <!-- BANK ACCOUNTS INFO -->
          <template v-if="primaryBankAccount">
            <p class="mb-4">Revise os dados da conta bancária a receber:</p>
            <div>
              <BankAccountCard
                :account="primaryBankAccount"
              />
            </div>
            <p class="text-center font-size-xSmall mt-3">
              <strong>Atenção:</strong> Para alterar sua conta primária, acesse <router-link :to="{ name: 'wallets.index' }" target="_blank" class="link-color2">suas contas</router-link>.
            </p>
            <div class="mt-3">
              <Button
                class="btn-outline-color2 btn-sm"
                @click="changeStep('user-info')"
              >
                Voltar
              </Button>
              <Button
                class="btn-primary btn-sm ml-2"
                @click="changeStep('confirm')"
              >
                Avançar
              </Button>
            </div>
          </template> <!-- END BANK ACCOUNTS INFO -->
          <!-- BANK ACCOUNT CREATE -->
          <template v-else>
            <p class="mb-2">Preencha os dados da conta</p>
            <BankAccountCreationForm
              @bank-account-created="parseCreatedAccount"
            />
          </template> <!-- END BANK ACCOUNT CREATE -->
        </template>
      </div> <!-- END BANK ACCOUNT STEP -->
      <!-- CONFIRMATION STEP -->
      <div v-if="step === 'confirm'">
        <div class="font-size-small">
          <h2 class="font-weight-bold font-size-medium">Responsável</h2>
          <p>
            <strong>CPF:</strong> {{ recipient.document | VMask('###.###.###-##') }}<br/>
            <strong>Celular:</strong> {{ recipient.phone | VMask('(##) #####-####') }}
          </p>
          <h2 class="font-weight-bold font-size-medium">Conta Bancária</h2>
          <BankAccountCard
            :account="primaryBankAccount"
          />
        </div>
        <div class="mt-3 text-center">
          <Button
            class="btn-outline-color2 btn-sm"
            @click="changeStep('bank-accounts')"
          >
            Voltar
          </Button>
          <Button
            class="btn-primary btn-sm ml-2"
            @click="submit"
            :loading="loader"
          >
            Finalizar
          </Button>
        </div>
      </div> <!-- END CONFIRMATION STEP -->
    </template>
  </b-modal>
</template>

<script>

import BankAccountsService from '@/modules/bank-accounts/services/bank-accounts-service'
import NotificationService from '@/modules/notifications/services/notifications-service'
import UsersService from '@/modules/users/services/users-service'
import { required } from 'vuelidate/lib/validators'
import { parseErrorResponseToArray } from '@/utils'
import { mapState } from 'vuex'

import AlertWraper from '@/components/common/Alert/AlertWraper'
import BankAccountCard from '@/components/bank-accounts/BankAccountCard'
import BankAccountCreationForm from '@/components/bank-accounts/BankAccountCreationForm'
import Button from '@/components/common/Button/Button'
import Loader from '@/components/common/Loader/Loader'
import { TheMask } from 'vue-the-mask'

export default {
  name: 'UserReceivableInfoModal',
  components: {
    AlertWraper,
    BankAccountCard,
    BankAccountCreationForm,
    Button,
    Loader,
    TheMask
  },
  data () {
    return {
      errors: null,
      loader: null,
      step: 'init',
      bank_accounts: [],
      recipient: {
        document: '',
        phone: ''
      }
    }
  },
  created () {
    this.getBankAccounts()
    if (this.user) {
      this.recipient.document = this.user.document
      this.recipient.phone = this.user.phone
    }
  },
  computed: {
    ...mapState({
      nightMode: state => state.nightMode
    }),
    modalClasses () {
      return {
        'default-modal': true
      }
    },
    stepTitle () {
      if (this.step === 'user-info') return 'Dados do recebedor'
      if (this.step === 'bank-accounts') {
        if (this.bank_accounts > 0) return 'Revise a conta bancária principal'
        return 'Crie uma conta bancária'
      }
      if (this.step === 'confirm') return 'Confirme os dados'
      return 'Informações de recebimento'
    },
    primaryBankAccount () {
      let bankAccount
      if (this.bank_accounts.length > 0) {
        this.bank_accounts.forEach(item => {
          if (item.is_primary) bankAccount = item
        })

        return bankAccount
      }
      return null
    },
    ...mapState('users', {
      user: state => state.user
    })
  },
  validations () {
    if (this.step === 'user-info') {
      return {
        recipient: {
          document: { required },
          phone: { required }
        }
      }
    }

    return ''
  },
  methods: {
    shown () {
    },
    hidden () {
      this.reset()
    },
    changeStep (step) {
      this.step = step
      if (step === 'bank-accounts') this.getBankAccounts()
    },
    async submit () {
      this.loader = true
      try {
        await UsersService.updateRecipient(this.recipient)
        this.$bvModal.hide('user-receivable-info')
        const notification = {
          notification: {
            type: 'success',
            content: 'Dados de recebimento configurados com sucesso!'
          }
        }
        NotificationService.createGlobalSuccessNotification(notification)
        this.$emit('receivable-created')
      } catch (error) {
        this.errors = parseErrorResponseToArray(error)
      }
      this.loader = false
    },
    async getBankAccounts () {
      this.loader = true
      try {
        const bankAccounts = await BankAccountsService.getBankAccounts()
        this.bank_accounts = bankAccounts
      } catch (error) {
        this.errors = parseErrorResponseToArray(error)
      }
      this.loader = false
    },
    parseCreatedAccount (account) {
      this.changeStep('bank-accounts')
    },
    reset () {
      this.recipient.document = ''
      this.recipient.phone = ''
      this.step = 'init'
      this.bank_accounts = []
    }
  }
}
</script>
